import { useEffect, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf"; ///dist/esm/entry.webpack'
import SignaturePad from "react-signature-pad-wrapper";
import {
  Paper,
  Button,
  Box,
  Grid,
  Typography,
  Card,
  Divider,
  FormControl,
  InputLabel,
  Select,
  TextField,
  MenuItem,
  Stack,
} from "@mui/material";
import { blueGrey } from "@mui/material/colors";

// Testing for prod fix...
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const colors = {
  unaccepted: "lightskyblue",
  accepted: "green",
};

const defSettingsForProgram = (program) => {
  const mapping = {
    cct: {
      rtm: null,
      largeTemperature: "",
      smallTemperature: "",
      largeCompression: "",
      smallCompression: "",
      largeFrequency: "",
      smallFrequency: "",
      largeAllowTempChange: "",
      largeAllowCompressionChange: "",
      smallAllowCompressionChange: "",
    },
    sam: {},
    gr: {
      direction_type: "",
      times_per_day: "",
      pressure: "",
      minutes: "",
      program: "",
      additional_instructions: "",
    },
  };

  return mapping[program] ?? {};
};

const vt4Config = {
  "Large Joint Settings": {
    largeTemperature: {
      fieldLabel: "Initial Temperature Setting",
      values: [43, 44, 45, 46, 47, 48, 49, 50],
      labels: ["43°F", "44°F", "45°F", "46°F", "47°F", "48°F", "49°F", "50°F"],
    },
    largeAllowTempChange: {
      fieldLabel: "Allow Patient to Change Temperature",
      values: [true, false],
      labels: ["Yes", "No"],
    },
    largeCompression: {
      fieldLabel: "Initial Compression Setting",
      values: [15, 35, 50],
      labels: ["15 mmHg (Low)", "35 mmHg (Med)", "50 mmHg (High)"],
    },
    largeAllowCompressionChange: {
      fieldLabel: "Allow Patient to Change Compression",
      values: [true, false],
      labels: ["Yes", "No"],
    },
    largeFrequency: {
      fieldLabel: "Treatment Frequency",
      values: [15, 30, 45],
      labels: [
        "15 Minutes On / 45 Minutes Off",
        "30 Minutes On / 45 Minutes Off",
        "45 Minutes On / 45 Minutes Off",
      ],
    },
  },
  "Small Joint Settings": {
    smallTemperature: {
      fieldLabel: "Initial Temperature Setting",
      values: [49, 50],
      labels: ["49°F", "50°F"],
    },
    smallCompression: {
      fieldLabel: "Initial Compression Setting",
      values: [15, 35, 50],
      labels: ["15 mmHg (Low)", "35 mmHg (Med)", "50 mmHg (High)"],
    },
    smallAllowCompressionChange: {
      fieldLabel: "Allow Patient to Change Compression",
      values: [true, false],
      labels: ["Yes", "No"],
    },
    smallFrequency: {
      fieldLabel: "Treatment Frequency",
      values: [15, 30, 45],
      labels: [
        "15 Minutes On / 45 Minutes Off",
        "30 Minutes On / 45 Minutes Off",
        "45 Minutes On / 45 Minutes Off",
      ],
    },
  },
};

function Vt4SettingsInput({ settings, onChange }) {
  const settingSections = [];

  settingSections.push(
    <Box key="rtm" my={2}>
      <Typography variant="h6">Remote Therapeutic Monitoring</Typography>
      <InputLabel>
        <Typography variant="body2">Include RTM</Typography>
      </InputLabel>
      <Select
        sx={{ width: "100%" }}
        value={settings.rtm}
        onChange={onChange}
        name="rtm"
      >
        <MenuItem value={true}>Yes, include RTM with all scripts</MenuItem>
        <MenuItem value={false}>No, I do not want RTM</MenuItem>
      </Select>
    </Box>,
  );

  for (const [key, value] of Object.entries(vt4Config)) {
    const title = <Typography variant="h6">{key}</Typography>;

    const inputs = [];
    for (const [key2, value2] of Object.entries(value)) {
      const label = (
        <InputLabel key="label">
          <Typography variant="body2">{value2.fieldLabel}</Typography>
        </InputLabel>
      );

      const options = value2.values.map((val, i) => {
        return (
          <MenuItem value={val} key={i}>
            {value2.labels[i]}
          </MenuItem>
        );
      });

      const input = (
        <Select
          sx={{ width: "100%" }}
          value={settings[key2]}
          onChange={onChange}
          name={key2}
        >
          {options}
        </Select>
      );

      inputs.push(
        <Box key={key2}>
          {label}
          {input}
        </Box>,
      );
    }

    const section = (
      <Box key={key} my={2}>
        {title}
        {inputs}
      </Box>
    );
    settingSections.push(section);
  }

  return <Box>{settingSections}</Box>;
}

function GrSettingsInput({ settings, onChange }) {
  return (
    <Stack direction="column" spacing={2} sx={{ my: 3 }}>
      <FormControl fullWidth>
        <InputLabel id="direction_type">Directions for Use</InputLabel>
        <Select
          id="direction_type"
          label="Directions for Use"
          onChange={onChange}
          name="direction_type"
          value={settings.direction_type}
        >
          <MenuItem value="">Choose One...</MenuItem>
          <MenuItem value="manual">Manual</MenuItem>
          <MenuItem value="defined">Defined</MenuItem>
          <MenuItem value="program">Program</MenuItem>
        </Select>
      </FormControl>
      {settings.direction_type === "defined" && (
        <>
          <FormControl fullWidth>
            <InputLabel id="times_per_day">
              Treatment Frequency (times per day)
            </InputLabel>
            <Select
              id="times_per_day"
              name="times_per_day"
              label="Treatment Frequency (times per day)"
              onChange={onChange}
              value={settings.times_per_day}
            >
              <MenuItem value="">Choose One...</MenuItem>
              <MenuItem value="1">Once per day</MenuItem>
              <MenuItem value="2">Twice per day</MenuItem>
              <MenuItem value="3">3 times per day</MenuItem>
              <MenuItem value="4">4 times per day</MenuItem>
              <MenuItem value="5">5 times per day</MenuItem>
              <MenuItem value="6">6 times per day</MenuItem>
              <MenuItem value="7">7 times per day</MenuItem>
              <MenuItem value="8">8 times per day</MenuItem>
              <MenuItem value="9">9 times per day</MenuItem>
              <MenuItem value="10">10 times per day</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel id="pressure">Pressure</InputLabel>
            <Select
              id="pressure"
              name="pressure"
              label="Pressure"
              onChange={onChange}
              value={settings.pressure}
            >
              <MenuItem value="">Choose One...</MenuItem>
              <MenuItem value="no">None</MenuItem>
              <MenuItem value="low">Low</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="high">High</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel id="pressure">Treatment Duration (minutes)</InputLabel>
            <Select
              name="minutes"
              label="Treatment Duration (minutes)"
              onChange={onChange}
              value={settings.minutes}
            >
              <MenuItem value="">Choose One...</MenuItem>
              <MenuItem value="5">5 minutes</MenuItem>
              <MenuItem value="10">10 minutes</MenuItem>
              <MenuItem value="15">15 minutes</MenuItem>
              <MenuItem value="20">20 minutes</MenuItem>
              <MenuItem value="25">25 minutes</MenuItem>
              <MenuItem value="30">30 minutes</MenuItem>
              <MenuItem value="35">35 minutes</MenuItem>
              <MenuItem value="40">40 minutes</MenuItem>
              <MenuItem value="45">45 minutes</MenuItem>
              <MenuItem value="50">50 minutes</MenuItem>
              <MenuItem value="55">55 minutes</MenuItem>
              <MenuItem value="60">60 minutes</MenuItem>
            </Select>
          </FormControl>
        </>
      )}
      {settings.direction_type === "program" && (
        <FormControl fullWidth>
          <InputLabel id="program">Program</InputLabel>
          <Select
            name="program"
            label="Program"
            onChange={onChange}
            value={settings.program}
          >
            <MenuItem value="">Choose One...</MenuItem>
            <MenuItem value="1">1</MenuItem>
            <MenuItem value="2">2</MenuItem>
            <MenuItem value="3">3</MenuItem>
            <MenuItem value="4">4</MenuItem>
            <MenuItem value="5">5</MenuItem>
            <MenuItem value="6">6</MenuItem>
          </Select>
        </FormControl>
      )}
      <TextField
        fullWidth
        label="Additional Instructions"
        name="additional_instructions"
        rows={4}
        multiline
        onChange={onChange}
        value={settings.additional_instructions}
      />
    </Stack>
  );
}

export default function Enroll({ program, blankRx, npi, onComplete, api }) {
  const [initBgColor, setInitBgColor] = useState(colors.unaccepted);
  const [initCursor, setInitCursor] = useState("auto");
  const [initPointerEvents, setInitPointerEvents] = useState("auto");
  const [extBgColor, setExtBgColor] = useState(colors.unaccepted);
  const [extCursor, setExtCursor] = useState("auto");
  const [extPointerEvents, setExtPointerEvents] = useState("auto");
  const [width, setWidth] = useState(300);
  const pdfRef = useRef(null);
  const initRef = useRef(null);
  const extRef = useRef(null);
  const [submission, setSubmission] = useState(["init", {}]);
  const [status, data] = submission;
  const [submitDisabled, setSubmitDisabled] = useState(true);
  const [settings, setSettings] = useState(defSettingsForProgram(program));

  const file = blankRx.location;

  function myOnComplete() {
    const sigs = [
      initRef.current.signaturePad.toDataURL(),
      extRef.current.signaturePad.toDataURL(),
    ];

    if (program === "sam") {
      api(npi, sigs)
        .then((d) => {
          setSubmission(["success", d]);
        })
        .catch((error) => {
          setSubmission(["error", error]);
        });
    }

    if (program === "cct") {
      api(npi, sigs, settings)
        .then((d) => {
          setSubmission(["success", d]);
        })
        .catch((error) => {
          setSubmission(["error", error]);
        });
    }

    if (program === "gr") {
      const payload = {};
      payload.direction_type = settings.direction_type;
      if (settings.direction_type === "manual") {
        payload.minutes = null;
        payload.pressure = null;
        payload.times_per_day = null;
        payload.program = null;
      }
      if (settings.direction_type === "defined") {
        payload.minutes = parseInt(settings.minutes, 10);
        payload.pressure = settings.pressure;
        payload.times_per_day = parseInt(settings.times_per_day, 10);
        payload.program = null;
      }
      if (settings.direction_type === "program") {
        payload.minutes = null;
        payload.pressure = null;
        payload.times_per_day = null;
        payload.program = parseInt(settings.program, 10);
      }
      payload.additional_instructions = settings.additional_instructions;
      api(npi, sigs, payload)
        .then((d) => {
          setSubmission(["success", d]);
        })
        .catch((error) => {
          setSubmission(["error", error]);
        });
    }
  }

  useEffect(() => {
    if (status === "success") {
      onComplete();
    }
  }, [status, onComplete]);

  useEffect(() => {
    const sigsGood =
      initBgColor === extBgColor && initBgColor === colors.accepted;

    let inputsGood = false;
    if (program === "cct") {
      inputsGood = Object.entries(settings).every(
        ([, value]) => value !== null && value !== "",
      );
    }
    if (program === "sam") {
      inputsGood = true;
    }
    if (program === "gr") {
      if (settings.direction_type === "manual") inputsGood = true;
      if (settings.direction_type === "defined") {
        inputsGood =
          settings.minutes !== "" &&
          settings.pressure !== "" &&
          settings.times_per_day !== "";
      }
      if (settings.direction_type === "program") {
        inputsGood = settings.program !== "";
      }
    }

    setSubmitDisabled(!(sigsGood && inputsGood));
  }, [initBgColor, extBgColor, settings]);

  function acceptSig(type) {
    if (type === "init") {
      setInitBgColor(colors.accepted);
      setInitCursor("not-allowed");
      setInitPointerEvents("none");
    } else if (type === "ext") {
      setExtBgColor(colors.accepted);
      setExtCursor("not-allowed");
      setExtPointerEvents("none");
    }
  }

  function clearSig(type) {
    if (type === "init") {
      initRef.current.signaturePad.clear();
      setInitBgColor(colors.unaccepted);
      setInitCursor("auto");
      setInitPointerEvents("auto");
    } else if (type === "ext") {
      extRef.current.signaturePad.clear();
      setExtBgColor(colors.unaccepted);
      setExtCursor("auto");
      setExtPointerEvents("auto");
    }
  }

  function setPdfWidth() {
    setWidth(pdfRef.current.offsetWidth);
  }

  const handleSettingsChange = (event) => {
    const otherSettings = {};
    if (program === "gr" && event.target.name === "direction_type") {
      if (event.target.value === "manual") {
        otherSettings.times_per_day = "";
        otherSettings.minutes = "";
        otherSettings.pressure = "";
        otherSettings.program = "";
      }
      if (event.target.value === "defined") {
        otherSettings.program = "";
      }
      if (event.target.value === "program") {
        otherSettings.times_per_day = "";
        otherSettings.minutes = "";
        otherSettings.pressure = "";
      }
    }

    setSettings({
      ...settings,
      ...otherSettings,
      [event.target.name]: event.target.value,
    });
  };

  return (
    <div>
      <Box ref={pdfRef}>
        <Document file={file} onLoadSuccess={setPdfWidth}>
          <Page
            pageNumber={1}
            renderTextLayer={false}
            renderAnnotationLayer={false}
            width={width}
          />
        </Document>
      </Box>

      <Divider />

      {program === "cct" && (
        <Vt4SettingsInput settings={settings} onChange={handleSettingsChange} />
      )}
      {program === "gr" && (
        <GrSettingsInput settings={settings} onChange={handleSettingsChange} />
      )}

      <Stack spacing={2}>
        <Box>
          <Typography variant="h6">Initial Rx Signature</Typography>
          <Card>
            <Paper
              sx={{
                backgroundColor: initBgColor,
                cursor: initCursor,
                pointerEvents: initPointerEvents,
              }}
            >
              <SignaturePad ref={initRef} redrawOnResize />
            </Paper>
            <Grid container>
              <Grid item xs={6} p={1}>
                <Button
                  variant="contained"
                  sx={{
                    backgroundColor: blueGrey[600],
                    color: blueGrey[100],
                    width: "100%",
                  }}
                  onClick={() => clearSig("init")}
                >
                  Clear
                </Button>
              </Grid>
              <Grid item xs={6} p={1}>
                <Button
                  variant="contained"
                  sx={{
                    backgroundColor: blueGrey[600],
                    color: blueGrey[100],
                    width: "100%",
                  }}
                  onClick={() => acceptSig("init")}
                >
                  Accept
                </Button>
              </Grid>
            </Grid>
          </Card>
        </Box>
        <Divider />
        <Box>
          <Typography variant="h6">Extension Rx Signature</Typography>
          <Card>
            <Paper
              sx={{
                backgroundColor: extBgColor,
                cursor: extCursor,
                pointerEvents: extPointerEvents,
              }}
            >
              <SignaturePad ref={extRef} redrawOnResize />
            </Paper>
            <Grid container>
              <Grid item xs={6} p={1}>
                <Button
                  variant="contained"
                  sx={{
                    backgroundColor: blueGrey[600],
                    color: blueGrey[100],
                    width: "100%",
                  }}
                  onClick={() => clearSig("ext")}
                >
                  Clear
                </Button>
              </Grid>
              <Grid item xs={6} p={1}>
                <Button
                  variant="contained"
                  sx={{
                    backgroundColor: blueGrey[600],
                    color: blueGrey[100],
                    width: "100%",
                  }}
                  onClick={() => acceptSig("ext")}
                >
                  Accept
                </Button>
              </Grid>
            </Grid>
          </Card>
        </Box>
        <Divider />
        <Box>
          <Button
            onClick={myOnComplete}
            variant="contained"
            sx={{
              backgroundColor: blueGrey[600],
              color: blueGrey[100],
              width: "100%",
            }}
            disabled={submitDisabled}
          >
            Finish
          </Button>
        </Box>
      </Stack>
    </div>
  );
}
